'use client';

import {
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Redo2,
  Save,
  Undo2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TEMPLATE_EDITOR_CATEGORIES,
  TEMPLATE_EDITOR_ITEMS,
  TemplateLivePreviewEditor,
  getTemplateDraftFallback,
  isExplanationTemplateKey,
  normalizeTextLayerDraft,
  resolveTemplateEditorLabel,
} from './original/RealtorAutoOriginalTemplateEditor';
import { RealtorAutoDeferredPanel } from './original/RealtorAutoDeferredPanel';
import { RealtorAutoTemplateSettingsFrame } from './original/RealtorAutoTemplateSettingsFrame';
import { RealtorAutoTemplateUnsavedNavigationDialog } from './original/RealtorAutoTemplateUnsavedNavigationDialog';

const CONTRACT_TEMPLATE_ITEMS = TEMPLATE_EDITOR_ITEMS.filter(item => item.categoryKey === 'CONTRACT_FORMS');
const TEMPLATE_ITEM_BY_KEY = Object.fromEntries(TEMPLATE_EDITOR_ITEMS.map(item => [item.key, item]));
const TEMPLATE_RUNTIME_IDLE_LOAD_MS = 750;

function record(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function rows(value) {
  return Array.isArray(value) ? value : [];
}

function text(value, fallback = '') {
  const next = String(value ?? '').trim();
  return next || fallback;
}

function cloneDraft(templateKey, value) {
  const fallback = getTemplateDraftFallback(templateKey);
  return JSON.parse(JSON.stringify(normalizeTextLayerDraft(value, fallback)));
}

function sameDraft(templateKey, left, right) {
  const fallback = getTemplateDraftFallback(templateKey);
  return JSON.stringify(normalizeTextLayerDraft(left, fallback)) === JSON.stringify(normalizeTextLayerDraft(right, fallback));
}

function normalizeDefaultTemplateKey(value) {
  const key = text(value, 'CONTRACT_LEASE').toUpperCase();
  return TEMPLATE_ITEM_BY_KEY[key]?.categoryKey === 'CONTRACT_FORMS' ? key : 'CONTRACT_LEASE';
}

function templatePayloadState(data) {
  const activeTemplates = rows(data?.activeTemplates);
  const drafts = Object.fromEntries(TEMPLATE_EDITOR_ITEMS.map(item => [item.key, cloneDraft(item.key, getTemplateDraftFallback(item.key))]));
  const versions = {};
  activeTemplates.forEach(template => {
    const key = text(template.templateKey ?? template.template_key);
    if (!TEMPLATE_ITEM_BY_KEY[key]) return;
    drafts[key] = cloneDraft(key, template.textLayer ?? template.text_layer);
    versions[key] = Number(template.version || 0);
  });
  return {
    drafts,
    versions,
    versionsByTemplateKey: Object.fromEntries(
      Object.entries(record(data?.versionsByTemplateKey)).map(([key, value]) => [key, rows(value)]),
    ),
  };
}

export function RealtorAutoTemplateManager({
  data,
  defaultTemplateKey,
  savingDefault,
  requestAdmin,
  onSaveDefault,
  onTemplatesChange,
}) {
  const initialState = useMemo(() => templatePayloadState(data), [data]);
  const initialDefaultKey = normalizeDefaultTemplateKey(defaultTemplateKey);
  const [showAdminAutomationHints, setShowAdminAutomationHints] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [drafts, setDrafts] = useState(initialState.drafts);
  const [savedDrafts, setSavedDrafts] = useState(initialState.drafts);
  const [dirtyKeys, setDirtyKeys] = useState({});
  const [historyByTemplateKey, setHistoryByTemplateKey] = useState({});
  const historyRef = useRef(historyByTemplateKey);
  const historyActionRef = useRef({});
  const [versionsByTemplateKey, setVersionsByTemplateKey] = useState(initialState.versionsByTemplateKey);
  const [activeVersionByKey, setActiveVersionByKey] = useState(initialState.versions);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(
    TEMPLATE_ITEM_BY_KEY[initialDefaultKey]?.categoryKey || 'CONTRACT_FORMS',
  );
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(initialDefaultKey);
  const [templateEditorMinimized, setTemplateEditorMinimized] = useState(false);
  const [templateEditorFullscreen, setTemplateEditorFullscreen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingVersionName, setPendingVersionName] = useState('');
  const [defaultKeyDraft, setDefaultKeyDraft] = useState(initialDefaultKey);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const searchParams = new URLSearchParams(window.location.search);
    setShowAdminAutomationHints(searchParams.get('admin') === '1' || searchParams.get('source') === 'admin-template');
  }, []);

  useEffect(() => {
    const next = templatePayloadState(data);
    setDrafts(next.drafts);
    setSavedDrafts(next.drafts);
    setDirtyKeys({});
    setHistoryByTemplateKey({});
    historyActionRef.current = {};
    setVersionsByTemplateKey(next.versionsByTemplateKey);
    setActiveVersionByKey(next.versions);
  }, [data]);

  useEffect(() => {
    setDefaultKeyDraft(normalizeDefaultTemplateKey(defaultTemplateKey));
  }, [defaultTemplateKey]);

  useEffect(() => {
    historyRef.current = historyByTemplateKey;
  }, [historyByTemplateKey]);

  const selectedCategoryItems = useMemo(
    () => TEMPLATE_EDITOR_ITEMS.filter(item => item.categoryKey === selectedCategoryKey),
    [selectedCategoryKey],
  );
  const selectedTemplateItem =
    selectedCategoryItems.find(item => item.key === selectedTemplateKey) || selectedCategoryItems[0] || TEMPLATE_EDITOR_ITEMS[0];
  const activeTemplateKey = selectedTemplateItem?.key || 'CONTRACT_SALE';
  const activeDraft = drafts[activeTemplateKey] || getTemplateDraftFallback(activeTemplateKey);
  const activeVersions = rows(versionsByTemplateKey[activeTemplateKey]);
  const activeIsLegalExplanation = isExplanationTemplateKey(activeTemplateKey);
  const activeHistory = historyByTemplateKey[activeTemplateKey] || { past: [], future: [] };
  const canUndoActiveTemplate = activeHistory.past.length > 0;
  const canRedoActiveTemplate = activeHistory.future.length > 0;
  const activeTemplateDirty = Boolean(dirtyKeys[activeTemplateKey]);
  const templateSettingsCategories = TEMPLATE_EDITOR_CATEGORIES.map(category => ({
    key: category.key,
    label: category.label,
    description: category.description,
    selected: category.key === selectedCategoryKey,
  }));
  const templateSettingsTemplates = selectedCategoryItems.map(item => ({
    key: item.key,
    label: item.label,
    version: activeVersionByKey[item.key] || 0,
    selected: item.key === activeTemplateKey,
  }));

  const recordDraftChange = (templateKey, nextDraft, options) => {
    setDrafts(current => {
      const fallback = getTemplateDraftFallback(templateKey);
      const previous = normalizeTextLayerDraft(current[templateKey] || fallback, fallback);
      const next = normalizeTextLayerDraft(nextDraft, previous);
      if (sameDraft(templateKey, previous, next)) return current;
      const actionId = options?.historyActionId;
      const historyBase = normalizeTextLayerDraft(options?.historyBase || previous, fallback);
      const historyPrevious = actionId ? historyBase : previous;
      const shouldRecordHistory = !actionId || historyActionRef.current[templateKey] !== actionId;
      if (actionId) historyActionRef.current[templateKey] = actionId;
      else delete historyActionRef.current[templateKey];
      if (shouldRecordHistory) {
        setHistoryByTemplateKey(history => {
          const entry = history[templateKey] || { past: [], future: [] };
          return {
            ...history,
            [templateKey]: { past: [...entry.past, historyPrevious].slice(-80), future: [] },
          };
        });
      }
      setDirtyKeys(keys => ({
        ...keys,
        [templateKey]: !sameDraft(templateKey, savedDrafts[templateKey] || fallback, next),
      }));
      return { ...current, [templateKey]: next };
    });
  };

  const restoreDraftWithoutHistory = (templateKey, nextDraft, dirty = false) => {
    const fallback = getTemplateDraftFallback(templateKey);
    const normalized = normalizeTextLayerDraft(nextDraft, fallback);
    setDrafts(current => ({ ...current, [templateKey]: normalized }));
    setDirtyKeys(keys => ({ ...keys, [templateKey]: dirty }));
    setHistoryByTemplateKey(history => ({ ...history, [templateKey]: { past: [], future: [] } }));
    delete historyActionRef.current[templateKey];
  };

  const undoTemplateDraft = templateKey => {
    delete historyActionRef.current[templateKey];
    setDrafts(current => {
      const entry = historyRef.current[templateKey] || { past: [], future: [] };
      const previous = entry.past[entry.past.length - 1];
      if (!previous) return current;
      const fallback = getTemplateDraftFallback(templateKey);
      const currentDraft = normalizeTextLayerDraft(current[templateKey] || fallback, fallback);
      setHistoryByTemplateKey(history => ({
        ...history,
        [templateKey]: { past: entry.past.slice(0, -1), future: [currentDraft, ...entry.future].slice(0, 80) },
      }));
      setDirtyKeys(keys => ({
        ...keys,
        [templateKey]: !sameDraft(templateKey, savedDrafts[templateKey] || fallback, previous),
      }));
      return { ...current, [templateKey]: previous };
    });
  };

  const redoTemplateDraft = templateKey => {
    delete historyActionRef.current[templateKey];
    setDrafts(current => {
      const entry = historyRef.current[templateKey] || { past: [], future: [] };
      const next = entry.future[0];
      if (!next) return current;
      const fallback = getTemplateDraftFallback(templateKey);
      const currentDraft = normalizeTextLayerDraft(current[templateKey] || fallback, fallback);
      setHistoryByTemplateKey(history => ({
        ...history,
        [templateKey]: { past: [...entry.past, currentDraft].slice(-80), future: entry.future.slice(1) },
      }));
      setDirtyKeys(keys => ({
        ...keys,
        [templateKey]: !sameDraft(templateKey, savedDrafts[templateKey] || fallback, next),
      }));
      return { ...current, [templateKey]: next };
    });
  };

  const saveTemplate = async (templateKey, versionName = '') => {
    if (isExplanationTemplateKey(templateKey)) {
      setError('');
      setMessage('중개대상물 확인설명서는 법정 원본 PDF를 그대로 사용하므로 템플릿 저장 대상이 아닙니다.');
      return false;
    }
    setSavingKey(templateKey);
    setError('');
    setMessage('');
    try {
      const response = await requestAdmin('/templates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateKey,
          title: resolveTemplateEditorLabel(templateKey),
          description: `${resolveTemplateEditorLabel(templateKey)} 텍스트 레이어`,
          versionName,
          reason: versionName || '수동 저장',
          textLayer: normalizeTextLayerDraft(drafts[templateKey] || getTemplateDraftFallback(templateKey), getTemplateDraftFallback(templateKey)),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.data) throw new Error(payload.error || '템플릿을 저장하지 못했습니다.');
      setMessage(`${resolveTemplateEditorLabel(templateKey)} 템플릿을 저장했습니다.`);
      onTemplatesChange(payload.data);
      return true;
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '템플릿을 저장하지 못했습니다.');
      return false;
    } finally {
      setSavingKey('');
    }
  };

  const refreshTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await requestAdmin('/templates', { method: 'GET', credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.data) throw new Error(payload.error || '템플릿을 불러오지 못했습니다.');
      onTemplatesChange(payload.data);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : '템플릿을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const executePendingNavigation = navigation => {
    if (!navigation) return;
    if (navigation.type === 'back') {
      window.location.assign('/admin/realtorauto/dashboard');
      return;
    }
    if (navigation.type === 'category') {
      setSelectedCategoryKey(navigation.key);
      const first = TEMPLATE_EDITOR_ITEMS.find(item => item.categoryKey === navigation.key);
      if (first) setSelectedTemplateKey(first.key);
      return;
    }
    setSelectedTemplateKey(navigation.key);
  };

  const requestNavigation = navigation => {
    if (activeTemplateDirty && !activeIsLegalExplanation) {
      setPendingVersionName('');
      setPendingNavigation(navigation);
      return;
    }
    executePendingNavigation(navigation);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && event.shiftKey) {
        event.preventDefault();
        redoTemplateDraft(activeTemplateKey);
      } else if (key === 'z') {
        event.preventDefault();
        undoTemplateDraft(activeTemplateKey);
      } else if (key === 'y') {
        event.preventDefault();
        redoTemplateDraft(activeTemplateKey);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (!activeTemplateDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeTemplateDirty]);

  const saveDefault = async () => {
    setError('');
    setMessage('');
    const saved = await onSaveDefault(defaultKeyDraft);
    if (saved) setMessage('신규 계약방 기본 템플릿을 저장했습니다.');
  };

  return <div className="space-y-4">
    <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[240px] flex-[1_1_520px] flex-wrap items-center gap-2 text-sm font-semibold text-slate-700">
          <span className="shrink-0">신규 계약방 기본 템플릿</span>
          <select
            value={defaultKeyDraft}
            onChange={event => setDefaultKeyDraft(event.target.value)}
            disabled={savingDefault}
            className="h-10 min-w-[220px] flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-950 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            {CONTRACT_TEMPLATE_ITEMS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={saveDefault}
          disabled={savingDefault || defaultKeyDraft === normalizeDefaultTemplateKey(defaultTemplateKey)}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-45"
        >
          <Save className="h-4 w-4" aria-hidden /> 기본값 저장
        </button>
      </div>
    </section>

    <RealtorAutoTemplateSettingsFrame
      loading={loading}
      message={message}
      error={error}
      categories={templateSettingsCategories}
      templates={templateSettingsTemplates}
      onRefresh={refreshTemplates}
      onBack={() => requestNavigation({ type: 'back' })}
      onSelectCategory={key => requestNavigation({ type: 'category', key })}
      onSelectTemplate={key => requestNavigation({ type: 'template', key })}
    >
      <article
        className={`rounded-lg border border-slate-200 bg-slate-50 p-4 ${
          templateEditorFullscreen ? 'fixed inset-0 z-50 overflow-y-auto rounded-none bg-slate-50' : ''
        }`}
        data-template-key={activeTemplateKey}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold text-blue-700">
              {TEMPLATE_EDITOR_CATEGORIES.find(item => item.key === selectedCategoryKey)?.label}
            </div>
            <h3 className="mt-1 text-xl font-semibold text-slate-950">{selectedTemplateItem?.documentTitle}</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setTemplateEditorMinimized(value => !value)}
                className="inline-flex h-9 w-10 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-100"
                title={templateEditorMinimized ? '다시 열기' : '최소화'}
              >
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => {
                  setTemplateEditorMinimized(false);
                  setTemplateEditorFullscreen(value => !value);
                }}
                className="inline-flex h-9 w-10 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-100"
                title={templateEditorFullscreen ? '이전 크기' : '전체화면'}
              >
                {templateEditorFullscreen ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
              </button>
              <button
                type="button"
                onClick={() => setTemplateEditorMinimized(true)}
                className="inline-flex h-9 w-10 items-center justify-center text-slate-600 hover:bg-rose-600 hover:text-white"
                title="닫기"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {activeTemplateDirty ? <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-black text-amber-700">저장 전 변경사항</span> : null}
            <button
              type="button"
              onClick={() => undoTemplateDraft(activeTemplateKey)}
              disabled={!canUndoActiveTemplate || Boolean(savingKey)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              title="되돌리기 (Ctrl+Z)"
            >
              <Undo2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => redoTemplateDraft(activeTemplateKey)}
              disabled={!canRedoActiveTemplate || Boolean(savingKey)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              title="앞으로가기 (Ctrl+Y / Ctrl+Shift+Z)"
            >
              <Redo2 className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => saveTemplate(activeTemplateKey)}
              disabled={Boolean(savingKey) || activeIsLegalExplanation}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingKey === activeTemplateKey ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
              {activeIsLegalExplanation ? '법정 원본 고정' : '템플릿 저장'}
            </button>
          </div>
        </div>

        {templateEditorMinimized ? <button
          type="button"
          onClick={() => setTemplateEditorMinimized(false)}
          className="mt-5 flex w-full items-center justify-between rounded-lg border border-dashed border-blue-200 bg-white px-4 py-4 text-left hover:border-blue-300 hover:bg-blue-50"
        >
          <span>
            <span className="block text-sm font-black text-slate-950">템플릿 편집기 최소화됨</span>
            <span className="mt-1 block text-xs font-semibold text-slate-500">클릭하면 현재 템플릿 편집 화면을 다시 엽니다.</span>
          </span>
          <Maximize2 className="h-4 w-4 text-blue-700" aria-hidden />
        </button> : <>
          <div className="mt-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
            {activeVersions.length ? <label className="block min-w-[240px] text-xs font-semibold text-slate-600">
              버전 선택/복원
              <select
                defaultValue=""
                onChange={event => {
                  const selectedVersion = activeVersions.find(version => text(version.id) === event.target.value);
                  if (!selectedVersion) return;
                  recordDraftChange(activeTemplateKey, cloneDraft(activeTemplateKey, selectedVersion.textLayer ?? selectedVersion.text_layer));
                }}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="">저장된 버전 {activeVersions.length.toLocaleString('ko-KR')}건</option>
                {activeVersions.map(version => {
                  const versionNo = Number(version.version || 0);
                  const id = text(version.id, String(versionNo));
                  const versionName = text(version.versionName ?? version.version_name);
                  return <option key={id} value={id}>v{versionNo} · {versionName || text(version.createdAt ?? version.created_at, text(version.status, '저장본'))}</option>;
                })}
              </select>
            </label> : <span className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">저장된 버전 없음</span>}
            <span className="text-xs leading-5 text-slate-500">원본 양식은 공유하고, 입력한 텍스트 레이어만 버전으로 저장합니다.</span>
          </div>
          <div className="mt-5">
            <RealtorAutoDeferredPanel active delayMs={TEMPLATE_RUNTIME_IDLE_LOAD_MS} minHeightClassName="min-h-[520px]" label="템플릿 편집기를 준비하는 중입니다.">
              <TemplateLivePreviewEditor
                templateKey={activeTemplateKey}
                title={selectedTemplateItem?.documentTitle || resolveTemplateEditorLabel(activeTemplateKey)}
                templateLabel={selectedTemplateItem?.label}
                layoutSummary={selectedTemplateItem?.layoutSummary}
                requiredSections={selectedTemplateItem?.requiredSections || []}
                draft={activeDraft}
                onDraftChange={(nextDraft, options) => recordDraftChange(activeTemplateKey, normalizeTextLayerDraft(nextDraft, activeDraft), options)}
                disabled={Boolean(savingKey)}
                showAutomationHints={showAdminAutomationHints}
                canUndo={canUndoActiveTemplate}
                canRedo={canRedoActiveTemplate}
                onUndo={() => undoTemplateDraft(activeTemplateKey)}
                onRedo={() => redoTemplateDraft(activeTemplateKey)}
              />
            </RealtorAutoDeferredPanel>
          </div>
        </>}
      </article>
    </RealtorAutoTemplateSettingsFrame>

    <RealtorAutoTemplateUnsavedNavigationDialog
      open={Boolean(pendingNavigation)}
      versionName={pendingVersionName}
      saving={savingKey === activeTemplateKey}
      canSave={Boolean(pendingVersionName.trim()) && !savingKey}
      onVersionNameChange={setPendingVersionName}
      onCancel={() => setPendingNavigation(null)}
      onDiscard={() => {
        const navigation = pendingNavigation;
        restoreDraftWithoutHistory(activeTemplateKey, savedDrafts[activeTemplateKey] || getTemplateDraftFallback(activeTemplateKey), false);
        setPendingNavigation(null);
        executePendingNavigation(navigation);
      }}
      onSave={async () => {
        const navigation = pendingNavigation;
        const saved = await saveTemplate(activeTemplateKey, pendingVersionName.trim());
        if (!saved) return;
        setPendingNavigation(null);
        executePendingNavigation(navigation);
      }}
    />
  </div>;
}
